import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, Trash2, MessageSquare, Sparkles, Loader2, AlertCircle, RefreshCw, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { useLanguage } from '../context/LanguageContext';
import {
  getLLMProviders,
  sendLLMMessage,
  getLLMSessions,
  getLLMSessionHistory,
  deleteLLMSession,
  analyzeSpendingWithAI,
  suggestBudgetWithAI
} from '../lib/api';

// Provider icons and colors
const PROVIDER_CONFIG = {
  mistral: { name: 'Mistral', color: '#FF7000', icon: '🔶' },
  claude: { name: 'Claude', color: '#8B5CF6', icon: '🟣' },
  gemini: { name: 'Gemini', color: '#4285F4', icon: '🔵' },
  openai: { name: 'OpenAI', color: '#10A37F', icon: '🟢' },
  ollama: { name: 'Ollama', color: '#333333', icon: '🖥️' }
};

export default function ChatPage() {
  const { language, t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [currentModel, setCurrentModel] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load provider info and sessions on mount
  useEffect(() => {
    loadProviderInfo();
    loadSessions();
  }, []);

  const loadProviderInfo = async () => {
    try {
      const response = await getLLMProviders();
      if (response.data.has_access === false) {
        setHasAccess(false);
        setMessages([{
          role: 'system',
          content: response.data.message || "L'assistant IA nécessite un abonnement Standard ou Premium."
        }]);
      } else {
        setHasAccess(true);
        setCurrentProvider(response.data.current_provider);
        setCurrentModel(response.data.current_model);
      }
    } catch (error) {
      console.error('Error loading provider info:', error);
      if (error.response?.status === 403) {
        setHasAccess(false);
        setMessages([{
          role: 'system',
          content: "L'assistant IA nécessite un abonnement Standard ou Premium."
        }]);
      }
    }
  };

  const loadSessions = async () => {
    try {
      const response = await getLLMSessions();
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadSessionHistory = async (sid) => {
    try {
      const response = await getLLMSessionHistory(sid);
      setMessages(response.data.messages || []);
      setSessionId(sid);
      setShowSessions(false);
    } catch (error) {
      toast.error(t('error_loading_conversation'));
    }
  };

  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await deleteLLMSession(sid);
      setSessions(sessions.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        setMessages([]);
        setSessionId(null);
      }
      toast.success(t('conversation_deleted'));
    } catch (error) {
      toast.error(t('error_deleting'));
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading || !hasAccess) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message to UI
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await sendLLMMessage(userMessage, null, null, sessionId);
      
      // Set session ID if new
      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
      }
      
      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        provider: response.data.provider,
        model: response.data.model
      }]);
      
      // Refresh sessions list
      loadSessions();
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'envoi du message';
      setMessages(prev => [...prev, {
        role: 'error',
        content: errorMessage
      }]);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleQuickAction = async (action) => {
    if (!hasAccess) return;
    setIsLoading(true);
    try {
      let response;
      if (action === 'analyze') {
        response = await analyzeSpendingWithAI();
        setMessages(prev => [...prev, 
          { role: 'user', content: '📊 Analyse mes dépenses du mois' },
          { role: 'assistant', content: response.data.analysis }
        ]);
      } else if (action === 'suggest') {
        response = await suggestBudgetWithAI();
        setMessages(prev => [...prev,
          { role: 'user', content: '💡 Suggère-moi un budget optimal' },
          { role: 'assistant', content: response.data.suggestion }
        ]);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
  };

  const providerConfig = currentProvider ? PROVIDER_CONFIG[currentProvider] : { name: 'IA', color: '#8B5CF6', icon: '🤖' };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/90 backdrop-blur-sm border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
              style={{ backgroundColor: providerConfig.color + '20' }}
            >
              {providerConfig.icon}
            </div>
            <div>
              <h1 className="font-semibold text-lg">
                {t('ai_assistant')}
              </h1>
              {hasAccess && currentProvider && (
                <p className="text-xs text-zinc-400">
                  {providerConfig.name} {currentModel && `• ${currentModel}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setShowSessions(!showSessions)}
              className="text-zinc-400 hover:text-white"
              data-testid="sessions-toggle"
            >
              <MessageSquare className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={startNewChat}
              className="text-zinc-400 hover:text-white"
              data-testid="new-chat-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Sessions List Dropdown */}
        {showSessions && sessions.length > 0 && (
          <div className="mt-4 bg-zinc-900 rounded-xl border border-zinc-800 max-h-64 overflow-y-auto" data-testid="sessions-list">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => loadSessionHistory(session.session_id)}
                className={`p-3 border-b border-zinc-800 last:border-0 hover:bg-zinc-800 cursor-pointer flex items-center justify-between ${
                  sessionId === session.session_id ? 'bg-zinc-800' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{session.last_message}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {PROVIDER_CONFIG[session.provider]?.icon || '🤖'} {session.message_count} messages
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => handleDeleteSession(session.session_id, e)}
                  className="text-zinc-500 hover:text-red-400 ml-2"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!hasAccess ? (
          // No access message
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-zinc-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {t('limited_access')}
            </h2>
            <p className="text-zinc-400 text-sm mb-6 max-w-md">
              {t('ai_assistant_premium_required')}
            </p>
            <Button 
              onClick={() => window.location.href = '/settings'}
              className="bg-gradient-to-r from-emerald-500 to-cyan-500"
            >
              {t('view_subscriptions')}
            </Button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {t('ai_financial_assistant')}
            </h2>
            <p className="text-zinc-400 text-sm mb-6 max-w-md">
              {t('ai_assistant_description')}
            </p>
            
            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700 hover:bg-zinc-800"
                onClick={() => handleQuickAction('analyze')}
                disabled={isLoading}
                data-testid="quick-analyze"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('analyze_spending')}
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-zinc-700 hover:bg-zinc-800"
                onClick={() => handleQuickAction('suggest')}
                disabled={isLoading}
                data-testid="quick-suggest"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('suggest_budget')}
              </Button>
            </div>
            
            {/* Example questions */}
            <div className="mt-8 text-left w-full max-w-md">
              <p className="text-xs text-zinc-500 mb-3">
                {t('example_questions')}
              </p>
              <div className="space-y-2">
                {(language === 'fr' ? [
                  "Comment puis-je économiser plus ce mois-ci ?",
                  "Analyse mes dépenses alimentaires",
                  "Quel budget devrais-je allouer aux loisirs ?"
                ] : language === 'pt' ? [
                  "Como posso poupar mais este mês?",
                  "Analisa as minhas despesas de alimentação",
                  "Que orçamento devo alocar para lazer?"
                ] : [
                  "How can I save more this month?",
                  "Analyze my food expenses",
                  "What budget should I allocate for entertainment?"
                ]).map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="w-full text-left p-3 bg-zinc-900 rounded-lg text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[70%] rounded-2xl p-4 ${
                    msg.role === 'user'
                      ? 'bg-emerald-500 text-white'
                      : msg.role === 'error'
                        ? 'bg-red-500/20 text-red-300 border border-red-500/50'
                        : msg.role === 'system'
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50'
                          : 'bg-zinc-800 text-white'
                  }`}
                >
                  {msg.role !== 'user' && msg.role !== 'error' && msg.role !== 'system' && (
                    <div className="flex items-center gap-2 mb-2 text-xs text-zinc-400">
                      <Bot className="w-3 h-3" />
                      {msg.provider && (
                        <span>{PROVIDER_CONFIG[msg.provider]?.icon} {msg.model}</span>
                      )}
                    </div>
                  )}
                  {msg.role === 'error' && (
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-xs font-medium">Erreur</span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 rounded-2xl p-4 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                  <span className="text-sm text-zinc-400">
                    {language === 'fr' ? 'Réflexion en cours...' : language === 'pt' ? 'Pensando...' : 'Thinking...'}
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      {hasAccess && (
        <div className="sticky bottom-20 bg-black/90 backdrop-blur-sm border-t border-zinc-800 p-4">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('ask_question_placeholder')}
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500 transition-colors"
              disabled={isLoading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="bg-emerald-500 hover:bg-emerald-600 rounded-xl px-4"
              data-testid="send-message-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </form>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
